// Intentionally simple token-level diff for UI review. This is not a full
// replacement for a general-purpose diff library. It avoids an O(n*m) matrix so
// fine-grained diffs can run on AI Refine's maximum supported text length.

export interface DiffPart {
  value: string;
  added?: boolean;
  removed?: boolean;
}

const matchingPairLimit = 5000000;

const tokenizeForDiff = (text: string) => text.match(/\s+|[^\s]+/g) || [];

const isWhitespaceToken = (token: string) => /^\s+$/.test(token);

const appendDiffPart = (
  parts: DiffPart[],
  value: string,
  flags: Pick<DiffPart, "added" | "removed">
) => {
  if (value === "") {
    return;
  }

  const previous = parts[parts.length - 1];

  if (
    previous &&
    previous.added === flags.added &&
    previous.removed === flags.removed
  ) {
    previous.value += value;
    return;
  }

  parts.push({ value, ...flags });
};

interface MatchLink {
  oldIndex: number;
  newIndex: number;
  previous?: MatchLink;
}

interface MatchingPair {
  oldIndex: number;
  newIndex: number;
}

const appendTokenRange = (
  parts: DiffPart[],
  tokens: string[],
  start: number,
  end: number,
  flags: Pick<DiffPart, "added" | "removed">
) => appendDiffPart(parts, tokens.slice(start, end).join(""), flags);

const lowerBound = (values: number[], value: number) => {
  let low = 0;
  let high = values.length;

  while (low < high) {
    const middle = Math.floor((low + high) / 2);
    if (values[middle] < value) {
      low = middle + 1;
    } else {
      high = middle;
    }
  }

  return low;
};

const findMatchingPairs = (
  oldTokens: string[],
  oldStart: number,
  oldEnd: number,
  newTokens: string[],
  newStart: number,
  newEnd: number
): MatchingPair[] | null => {
  const newIndexesByToken = new Map<string, number[]>();

  for (let newIndex = newStart; newIndex < newEnd; newIndex++) {
    const token = newTokens[newIndex];
    if (isWhitespaceToken(token)) {
      continue;
    }

    const indexes = newIndexesByToken.get(token);
    if (indexes) {
      indexes.push(newIndex);
    } else {
      newIndexesByToken.set(token, [newIndex]);
    }
  }

  let matchingPairCount = 0;
  for (let oldIndex = oldStart; oldIndex < oldEnd; oldIndex++) {
    matchingPairCount +=
      newIndexesByToken.get(oldTokens[oldIndex])?.length ?? 0;
    if (matchingPairCount > matchingPairLimit) {
      return null;
    }
  }

  const thresholds: number[] = [];
  const links: MatchLink[] = [];

  for (let oldIndex = oldStart; oldIndex < oldEnd; oldIndex++) {
    const newIndexes = newIndexesByToken.get(oldTokens[oldIndex]);
    if (!newIndexes) {
      continue;
    }

    for (
      let newIndexesIndex = newIndexes.length - 1;
      newIndexesIndex >= 0;
      newIndexesIndex--
    ) {
      const newIndex = newIndexes[newIndexesIndex];
      const thresholdIndex = lowerBound(thresholds, newIndex);

      if (thresholds[thresholdIndex] === newIndex) {
        continue;
      }

      thresholds[thresholdIndex] = newIndex;
      links[thresholdIndex] = {
        oldIndex,
        newIndex,
        previous: thresholdIndex > 0 ? links[thresholdIndex - 1] : undefined,
      };
    }
  }

  const pairs: MatchingPair[] = [];
  let link = links[links.length - 1];

  while (link) {
    pairs.push({
      oldIndex: link.oldIndex,
      newIndex: link.newIndex,
    });
    link = link.previous;
  }

  return pairs.reverse();
};

const appendPositionalDiff = (
  parts: DiffPart[],
  oldTokens: string[],
  oldStart: number,
  oldEnd: number,
  newTokens: string[],
  newStart: number,
  newEnd: number
) => {
  let oldIndex = oldStart;
  let newIndex = newStart;

  while (oldIndex < oldEnd && newIndex < newEnd) {
    if (oldTokens[oldIndex] === newTokens[newIndex]) {
      appendDiffPart(parts, oldTokens[oldIndex], {});
      oldIndex++;
      newIndex++;
    } else {
      appendDiffPart(parts, oldTokens[oldIndex], { removed: true });
      appendDiffPart(parts, newTokens[newIndex], { added: true });
      oldIndex++;
      newIndex++;
    }
  }

  while (oldIndex < oldEnd) {
    appendDiffPart(parts, oldTokens[oldIndex], { removed: true });
    oldIndex++;
  }

  while (newIndex < newEnd) {
    appendDiffPart(parts, newTokens[newIndex], { added: true });
    newIndex++;
  }
};

const appendDiff = (
  parts: DiffPart[],
  oldTokens: string[],
  oldStart: number,
  oldEnd: number,
  newTokens: string[],
  newStart: number,
  newEnd: number
) => {
  while (
    oldStart < oldEnd &&
    newStart < newEnd &&
    oldTokens[oldStart] === newTokens[newStart]
  ) {
    appendDiffPart(parts, oldTokens[oldStart], {});
    oldStart++;
    newStart++;
  }

  let commonSuffixLength = 0;
  while (
    oldStart + commonSuffixLength < oldEnd &&
    newStart + commonSuffixLength < newEnd &&
    oldTokens[oldEnd - commonSuffixLength - 1] ===
      newTokens[newEnd - commonSuffixLength - 1]
  ) {
    commonSuffixLength++;
  }

  oldEnd -= commonSuffixLength;
  newEnd -= commonSuffixLength;

  if (oldStart === oldEnd || newStart === newEnd) {
    appendTokenRange(parts, oldTokens, oldStart, oldEnd, { removed: true });
    appendTokenRange(parts, newTokens, newStart, newEnd, { added: true });
  } else {
    const matchingPairs = findMatchingPairs(
      oldTokens,
      oldStart,
      oldEnd,
      newTokens,
      newStart,
      newEnd
    );

    if (matchingPairs === null) {
      appendPositionalDiff(
        parts,
        oldTokens,
        oldStart,
        oldEnd,
        newTokens,
        newStart,
        newEnd
      );
    } else if (matchingPairs.length === 0) {
      appendPositionalDiff(
        parts,
        oldTokens,
        oldStart,
        oldEnd,
        newTokens,
        newStart,
        newEnd
      );
    } else {
      let nextOldStart = oldStart;
      let nextNewStart = newStart;

      for (const pair of matchingPairs) {
        appendDiff(
          parts,
          oldTokens,
          nextOldStart,
          pair.oldIndex,
          newTokens,
          nextNewStart,
          pair.newIndex
        );
        appendDiffPart(parts, oldTokens[pair.oldIndex], {});
        nextOldStart = pair.oldIndex + 1;
        nextNewStart = pair.newIndex + 1;
      }

      appendDiff(
        parts,
        oldTokens,
        nextOldStart,
        oldEnd,
        newTokens,
        nextNewStart,
        newEnd
      );
    }
  }

  appendTokenRange(parts, oldTokens, oldEnd, oldEnd + commonSuffixLength, {});
};

export const diffWords = (oldText: string, newText: string): DiffPart[] => {
  if (oldText === newText) {
    return [{ value: oldText }];
  }

  const oldTokens = tokenizeForDiff(oldText);
  const newTokens = tokenizeForDiff(newText);
  const parts: DiffPart[] = [];
  appendDiff(
    parts,
    oldTokens,
    0,
    oldTokens.length,
    newTokens,
    0,
    newTokens.length
  );

  return parts;
};
